import {useEventListener} from '@chakra-ui/react';
import {useCallback, useState} from 'react';
import useResizeObserver from 'use-resize-observer';

type TooltipPosition = {
  left: number;
  top: number;
  node: HTMLElement | null;
  recalculate: (offset?: {x: number; y: number}) => {left: number; top: number};
};

type TooltipDimensions = {
  width: number;
  height: number;
};

export const useTooltipPosition = ({
  tooltipRef,
  containerRef,
  offset,
}: {
  containerRef?: React.RefObject<HTMLElement>;
  tooltipRef: React.RefObject<HTMLElement>;
  offset: {x: number; y: number};
}): TooltipPosition => {
  const [position, setPosition] = useState<Omit<TooltipPosition, 'node' | 'recalculate'>>({
    left: offset.x,
    top: offset.y,
  });
  const element = tooltipRef.current;
  const container = containerRef?.current;

  const {height = 50, width = 150} = useResizeObserver({
    ref: tooltipRef,
  });

  useResizeObserver({
    ref: containerRef,
    onResize: () => {
      if (!container) return;
      const position = calculatePosition(containerRef, offset, {height, width});
      setPosition(position);
    },
  });

  useEventListener(`resize`, () => {
    if (!container) return;
    const position = calculatePosition(containerRef, offset, {height, width});
    setPosition(position);
  });

  useEventListener(`scroll`, () => {
    if (!container) return;
    const position = calculatePosition(containerRef, offset, {height, width});
    setPosition(position);
  });

  const recalculate = useCallback(
    ({y, x}: {x: number; y: number} = {x: offset?.x ?? 0, y: offset?.y ?? 0}) => {
      if (!container) {
        setPosition({left: x, top: y});
        return {left: x, top: y};
      }

      const containerRect = container.getBoundingClientRect();
      const left = containerRect.left + x;
      const top = containerRect.top + y;
      setPosition({left, top});
      return {left, top};
    },
    [container, offset?.x, offset?.y],
  );

  return {...position, node: element, recalculate};
};

const calculatePosition = (
  containerRef: React.RefObject<HTMLElement>,
  offset: {x: number; y: number},
  tooltipDimensions: TooltipDimensions,
): {left: number; top: number} => {
  if (!containerRef?.current) {
    return {left: offset.x, top: offset.y};
  }

  const containerRect = containerRef.current.getBoundingClientRect();
  const left = containerRect.left + offset.x;
  const top = containerRect.top + offset.y;

  // Check if the tooltip is outside the window and adjust it
  const windowWidth = window.innerWidth;
  const windowHeight = window.innerHeight;

  const adjustedLeft = left + tooltipDimensions.width > windowWidth ? windowWidth - tooltipDimensions.width - 10 : left;
  const adjustedTop =
    top + tooltipDimensions.height > windowHeight ? windowHeight - tooltipDimensions.height - 10 : top;

  return {left: adjustedLeft, top: adjustedTop};
};
